/* eslint-disable react/destructuring-assignment */
import { Children, PropsWithChildren, ReactNode, useMemo } from "react";

import { useLoc } from "@app/shared/lib/hooks";
import { LocStringCase, PluralKeys } from "@app/shared/lib/localization";

import { StringTemplate } from "../StringTemplate";

import { TimeLocProps, TimeLoc } from "./TimeLoc";

type LocProps = {
  word?: string;
  wordPlural?: PluralKeys;
  number?: number;
  default?: string;
  defaultPlural?: PluralKeys;
  case?: LocStringCase;
  args?: ReactNode[];
  className?: string;
};

type LocPluralProps = Omit<
  LocProps,
  "wordPlural" | "defaultPlural" | "number"
> & { number: number; renderNumber?: boolean };

export const Loc: React.FC<PropsWithChildren<LocProps>> & {
  Plural: React.FC<LocPluralProps>;
  Time: React.FC<TimeLocProps>;
} = (props) => {
  const loc = useLoc();

  const word = useMemo(
    () =>
      props.word
        ? loc.word(props.word, { default: props.default, case: props.case })
        : loc.wordPlural(props.number!, props.wordPlural!, {
            default: props.defaultPlural,
            case: props.case,
          }),
    [
      props.word,
      props.default,
      props.case,
      props.number,
      props.wordPlural,
      props.defaultPlural,
      loc,
    ],
  );

  const args = useMemo(
    () => props.args ?? Children.toArray(props.children),
    [props.args, props.children],
  );
  if (props.word === undefined && props.wordPlural === undefined) {
    // eslint-disable-next-line no-console
    console.warn(`Loc 'word' or 'wordPlural' must be specified`);

    return null;
  }

  if (props.wordPlural !== undefined && props.number === undefined) {
    // eslint-disable-next-line no-console
    console.warn(`Loc 'number' must be specified when use 'wordPlural'`);

    return null;
  }

  if (args.length === 0) return <span className={props.className}>{word}</span>;

  return (
    <StringTemplate template={word} args={args} className={props.className} />
  );
};

const LocPlural: React.FC<LocPluralProps> = (props) => {
  const loc = (
    <Loc
      {...props}
      word={undefined}
      wordPlural={{
        one: `${props.word}.plural.one`,
        two: `${props.word}.plural.two`,
        other: `${props.word}.plural.other`,
      }}
      defaultPlural={{ one: "item", two: "items", other: "items" }}
      case={props.case ?? "lower"}
    />
  );
  if (props.renderNumber === true)
    return (
      <span className={props.className}>
        {props.number}&nbsp;
        {loc}
      </span>
    );

  return loc;
};

Loc.Plural = LocPlural;

Loc.Time = (props) => <TimeLoc {...props} />;
